/**
 * Nuevo grupo ad-hoc (F4): nombre, descripción, responsable. Quien lo crea
 * queda como manager (y el responsable también).
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { trpc } from '@/lib/trpc';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { errMsg } from '@/hooks/useVault';

interface NewGroupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (groupId: string) => void;
}

export function NewGroupDialog({ open, onOpenChange, onCreated }: NewGroupDialogProps) {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [responsible, setResponsible] = useState('');
  useEffect(() => {
    if (open) { setName(''); setDescription(''); setResponsible(''); }
  }, [open]);

  const create = trpc.vault.createGroup.useMutation({
    onSuccess: (g) => {
      utils.vault.listGroups.invalidate();
      utils.vault.getMyAccess.invalidate();
      toast.success(t('vault.groups.created', 'Grupo creado'));
      onOpenChange(false);
      onCreated(g.groupId);
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const canSave = name.trim().length >= 2 && !create.isPending && (!responsible || /^[^@\s]+@winclap\.com$/i.test(responsible.trim()));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('vault.groups.new_title', 'Nuevo grupo ad-hoc')}</DialogTitle>
          <DialogDescription>{t('vault.groups.new_sub', 'Para un proyecto o un cliente. Vos quedás como manager; después sumás miembros y le das acceso a credenciales desde cada una.')}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 py-2">
          <div className="space-y-1">
            <Label htmlFor="g-name">{t('vault.groups.name', 'Nombre')}</Label>
            <Input id="g-name" value={name} onChange={(e) => setName(e.target.value)} placeholder={t('vault.groups.name_ph', 'Paid Media — LATAM')} maxLength={80} autoFocus />
          </div>
          <div className="space-y-1">
            <Label htmlFor="g-desc">{t('vault.groups.description', 'Descripción (opcional)')}</Label>
            <Input id="g-desc" value={description} onChange={(e) => setDescription(e.target.value)} maxLength={300} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="g-resp">{t('vault.groups.responsible_opt', 'Responsable (opcional, por defecto vos)')}</Label>
            <Input id="g-resp" value={responsible} onChange={(e) => setResponsible(e.target.value)} placeholder="nombre.apellido@winclap.com" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t('common.ui.cancel', 'Cancelar')}</Button>
          <Button onClick={() => create.mutate({ name: name.trim(), description: description.trim() || undefined, responsibleEmail: responsible.trim() || undefined })} disabled={!canSave}>
            {create.isPending ? t('vault.form.saving', 'Guardando…') : t('vault.groups.create', 'Crear grupo')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
